/**
 * What the chat says when a launch is refused for a reason it can name by
 * code (ADR-20260925): the words come from the code, never from the server's
 * sentence. The codes:
 *
 * - default_model_missing, a mission with no agents whose organization has
 *   no default chat model (agion/Agion#2622). Only an owner or admin can
 *   choose the default (governance RequireAdmin), so only they get the link
 *   to the console's model settings.
 * - GRANT_ACCESS_NOT_READY (503), GRANT_ADMIN_DENIED (403) and
 *   GRANT_TRUST_TIER_TOO_LOW (403): the mission's agent access is not in
 *   place yet, an administrator removed it, or the agent's trust level allows
 *   none of what the mission needs (agion/Agion#2689 C3). The server decides
 *   all three and names the resources in resources (id, display_name) with
 *   resource_count; the chat only words them.
 */

import { isChatApiError } from '../../services/api-failure'

/** The refusal code orchestration-service answers for a missing default chat model. */
export const DEFAULT_MODEL_MISSING_CODE = 'default_model_missing'

/** The refusal code orchestration-service answers when the mission's agent access is not in place yet. */
export const GRANT_ACCESS_NOT_READY_CODE = 'GRANT_ACCESS_NOT_READY'

/** The refusal code orchestration-service answers when an administrator removed the agent's access. */
export const GRANT_ADMIN_DENIED_CODE = 'GRANT_ADMIN_DENIED'

/** The refusal code orchestration-service answers when the agent's trust level allows none of what the mission needs. */
export const GRANT_TRUST_TIER_TOO_LOW_CODE = 'GRANT_TRUST_TIER_TOO_LOW'

/** The console page where the organization's default chat model is chosen. */
export const MODEL_SETTINGS_PATH = '/governance/resources?tab=models'

/** Roles that can choose the organization's default chat model. */
const MODEL_SETTINGS_ROLES = new Set(['owner', 'admin'])

export const LAUNCH_REFUSAL_TEXTS = {
  defaultModelMissingAdmin: {
    key: 'chat:launchRefusal.defaultModelMissing.admin',
    fallback: 'Your organization has no default chat model, so this mission has no agent to run as. Choose one in the admin console under Governance > Resources > Models.',
  },
  defaultModelMissingMember: {
    key: 'chat:launchRefusal.defaultModelMissing.member',
    fallback: 'Your organization has no default chat model, so this mission has no agent to run as. Ask an administrator to choose one.',
  },
  openModelSettings: {
    key: 'chat:launchRefusal.openModelSettings',
    fallback: 'Open model settings',
  },
  grantAccessNotReadyNamed: {
    key: 'chat:launchRefusal.grantAccessNotReady.named',
    fallback: "This mission's agent access to {{resources}} is not ready yet, so the run did not start. Try again in a moment. If this keeps happening, contact support@agion.ai.",
  },
  grantAccessNotReady: {
    key: 'chat:launchRefusal.grantAccessNotReady.unnamed',
    fallback: "This mission's agent access is not ready yet, so the run did not start. Try again in a moment. If this keeps happening, contact support@agion.ai.",
  },
  grantAdminDeniedNamed: {
    key: 'chat:launchRefusal.grantAdminDenied.named',
    fallback: "An administrator has removed this agent's access to {{resources}}, so the run did not start. Ask your administrator, or contact support@agion.ai.",
  },
  grantAdminDenied: {
    key: 'chat:launchRefusal.grantAdminDenied.unnamed',
    fallback: "An administrator has removed this agent's access to a resource this mission uses, so the run did not start. Ask your administrator, or contact support@agion.ai.",
  },
  grantTrustTierTooLowNamed: {
    key: 'chat:launchRefusal.grantTrustTierTooLow.named',
    fallback: "This agent's trust level does not allow what this mission needs on {{resources}}, so the run did not start. Ask your administrator, or contact support@agion.ai.",
  },
  grantTrustTierTooLow: {
    key: 'chat:launchRefusal.grantTrustTierTooLow.unnamed',
    fallback: "This agent's trust level does not allow what this mission needs on a resource this mission uses, so the run did not start. Ask your administrator, or contact support@agion.ai.",
  },
} as const

type RefusalText = { key: string; fallback: string }

/** The unnamed and named texts of each grant access refusal code. */
const GRANT_REFUSAL_TEXTS: Partial<Record<string, { unnamed: RefusalText; named: RefusalText }>> = {
  [GRANT_ACCESS_NOT_READY_CODE]: { unnamed: LAUNCH_REFUSAL_TEXTS.grantAccessNotReady, named: LAUNCH_REFUSAL_TEXTS.grantAccessNotReadyNamed },
  [GRANT_ADMIN_DENIED_CODE]: { unnamed: LAUNCH_REFUSAL_TEXTS.grantAdminDenied, named: LAUNCH_REFUSAL_TEXTS.grantAdminDeniedNamed },
  [GRANT_TRUST_TIER_TOO_LOW_CODE]: { unnamed: LAUNCH_REFUSAL_TEXTS.grantTrustTierTooLow, named: LAUNCH_REFUSAL_TEXTS.grantTrustTierTooLowNamed },
}

type Translate = (key: string, fallback: string, options?: Record<string, unknown>) => string

/**
 * The resources a grant access refusal names, by their display names, with
 * the count of those it does not name as "(+n)"; undefined when it names
 * none by name.
 */
function refusalResourceList(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null) return undefined
  const body = data as Record<string, unknown>
  const resources = Array.isArray(body['resources']) ? body['resources'] : []
  const names = resources
    .map((r) => (typeof r === 'object' && r !== null ? (r as Record<string, unknown>)['display_name'] : undefined))
    .filter((name): name is string => typeof name === 'string' && name.trim() !== '')
    .map((name) => name.trim())
  if (names.length === 0) return undefined
  const count = typeof body['resource_count'] === 'number' ? body['resource_count'] : names.length
  const more = count - names.length
  return more > 0 ? `${names.join(', ')} (+${more})` : names.join(', ')
}

/** The chat's words for a grant access refusal (agion/Agion#2689 C3). */
function grantAccessRefusal(t: Translate, texts: { unnamed: RefusalText; named: RefusalText }, data: unknown): LaunchRefusal {
  const resources = refusalResourceList(data)
  if (!resources) {
    const { key, fallback } = texts.unnamed
    return { reason: t(key, fallback) }
  }
  const { key, fallback } = texts.named
  return { reason: t(key, fallback, { resources }) }
}

/** A refused launch as the chat shows it. */
export interface LaunchRefusal {
  /** The sentence after "The conversation could not start." */
  reason: string
  /** Where the person can fix it, when they are allowed to. */
  settings?: { href: string; label: string }
}

/**
 * The chat's words for a launch refusal it knows by code, for this person's
 * role, or undefined when the refusal carries no code the chat maps (the
 * caller then shows the server's reason, as before).
 */
export function launchRefusal(
  t: Translate,
  error: unknown,
  role: string | undefined,
  adminUrl: string,
): LaunchRefusal | undefined {
  if (!isChatApiError(error)) return undefined
  const grantTexts = error.code ? GRANT_REFUSAL_TEXTS[error.code] : undefined
  if (grantTexts) return grantAccessRefusal(t, grantTexts, error.data)
  if (error.code !== DEFAULT_MODEL_MISSING_CODE) return undefined
  const canChoose = MODEL_SETTINGS_ROLES.has((role ?? '').toLowerCase())
  if (!canChoose) {
    const { key, fallback } = LAUNCH_REFUSAL_TEXTS.defaultModelMissingMember
    return { reason: t(key, fallback) }
  }
  const { key, fallback } = LAUNCH_REFUSAL_TEXTS.defaultModelMissingAdmin
  const label = LAUNCH_REFUSAL_TEXTS.openModelSettings
  return {
    reason: t(key, fallback),
    ...(adminUrl ? { settings: { href: `${adminUrl}${MODEL_SETTINGS_PATH}`, label: t(label.key, label.fallback) } } : {}),
  }
}
